import React, { useState, useEffect } from 'react';
import {
  LiveKitRoom,
  useVoiceAssistant,
  BarVisualizer,
  useParticipants,
} from '@livekit/components-react';
import { ConnectionState, Track, Room } from 'livekit-client';
import { Mic, MicOff, Brain, Sparkles, Phone, PhoneOff } from 'lucide-react';
import { logger } from '../utils/logger';

interface ModernVoiceAssistantProps {
  roomName: string;
  token: string;
  wsUrl: string;
  onDisconnect?: () => void;
  className?: string;
}

const VoiceAssistantUI: React.FC<{
  onDisconnect?: () => void;
}> = ({ onDisconnect }) => {
  const participants = useParticipants();
  const voiceAssistant = useVoiceAssistant();
  const { state, audioTrack } = voiceAssistant;
  
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    // Use voice assistant state to determine connection
    setIsConnected(state === 'listening' || state === 'speaking' || state === 'thinking');
    logger.info('Voice assistant state:', state);
  }, [state]);

  const toggleListening = () => {
    // Voice assistant API doesn't expose start/stop directly
    // The component will automatically listen when the room is connected
    setIsListening(!isListening);
    logger.info('Toggled listening state:', !isListening);
  };

  const handleDisconnect = () => {
    // Disconnect is handled by the parent component
    setIsListening(false);
    onDisconnect?.();
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Brain className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Rraasi Voice Assistant</h2>
              <p className="text-sm text-gray-300">
                {isConnected ? 'Connected' : 'Connecting...'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Connection Status */}
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              isConnected ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-400' : 'bg-yellow-400'
              }`} />
              <span className="text-sm">
                {isConnected ? 'Live' : 'Connecting'}
              </span>
            </div>
            
            {/* Disconnect Button */}
            <button
              onClick={handleDisconnect}
              className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 transition-colors"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-8">
        {/* Voice Assistant Status */}
        <div className="text-center space-y-4">
          <div className="w-32 h-32 mx-auto relative">
            {/* Animated Circle */}
            <div className={`w-full h-full rounded-full border-4 transition-all duration-300 ${
              isListening 
                ? 'border-purple-400 bg-purple-400/20 animate-pulse' 
                : isConnected 
                  ? 'border-green-400 bg-green-400/20' 
                  : 'border-gray-400 bg-gray-400/20'
            }`}>
              <div className="w-full h-full flex items-center justify-center">
                {isListening ? (
                  <Mic className="h-16 w-16 text-purple-400" />
                ) : (
                  <Sparkles className="h-16 w-16 text-green-400" />
                )}
              </div>
            </div>
            
            {/* Audio Visualizer */}
            {audioTrack && isListening && (
              <div className="absolute inset-0 flex items-center justify-center">
                <BarVisualizer state={state} trackRef={audioTrack} />
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold">
              {isListening ? 'Listening...' : isConnected ? 'Ready to Chat' : 'Connecting...'}
            </h3>
            <p className="text-gray-300">
              {isListening 
                ? 'Speak now - I\'m listening to your question'
                : isConnected 
                  ? 'Click the microphone to start talking'
                  : 'Establishing connection...'
              }
            </p>
          </div>
        </div>

        {/* Voice Assistant State */}
        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-400">Assistant State:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              state === 'listening' ? 'bg-purple-500/20 text-purple-400' :
              state === 'thinking' ? 'bg-yellow-500/20 text-yellow-400' :
              state === 'speaking' ? 'bg-blue-500/20 text-blue-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {state || 'idle'}
            </span>
          </div>
        </div>

        {/* Control Button */}
        <button
          onClick={toggleListening}
          disabled={!isConnected}
          className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
            isListening
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : isConnected
                ? 'bg-purple-500 hover:bg-purple-600 text-white'
                : 'bg-gray-500 text-gray-300 cursor-not-allowed'
          }`}
        >
          {isListening ? (
            <MicOff className="h-8 w-8" />
          ) : (
            <Mic className="h-8 w-8" />
          )}
        </button>

        {/* Participants Count */}
        <div className="text-center text-sm text-gray-400">
          <span>Participants: {participants.length}</span>
        </div>
      </div>
    </div>
  );
};

const ModernVoiceAssistant: React.FC<ModernVoiceAssistantProps> = ({
  roomName,
  token,
  wsUrl,
  onDisconnect,
  className = '',
}) => {
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const handleConnected = () => {
    setIsConnecting(false);
    setConnectionError(null);
    logger.info('Connected to voice assistant room:', roomName);
  };

  const handleDisconnected = () => {
    setIsConnecting(false);
    logger.info('Disconnected from voice assistant room');
    onDisconnect?.();
  };

  const handleError = (error: Error) => {
    setIsConnecting(false);
    setConnectionError(error.message);
    logger.error('Voice assistant connection error:', error);
  };

  if (connectionError) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-red-900 to-red-800 text-white">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
            <PhoneOff className="h-8 w-8 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-400">Connection Failed</h3>
            <p className="text-red-300 text-sm">{connectionError}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${className}`}>
      <LiveKitRoom
        serverUrl={wsUrl}
        token={token}
        connect={true}
        onConnected={handleConnected}
        onDisconnected={handleDisconnected}
        onError={handleError}
        options={{
          adaptiveStream: true,
          dynacast: true,
        }}
      >
        {isConnecting ? (
          <div className="h-full flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center animate-pulse">
                <Brain className="h-8 w-8 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Connecting to Voice Assistant</h3>
                <p className="text-gray-300 text-sm">Please wait...</p>
              </div>
            </div>
          </div>
        ) : (
          <VoiceAssistantUI onDisconnect={onDisconnect} />
        )}
      </LiveKitRoom>
    </div>
  );
};

export default ModernVoiceAssistant;
